import React, { useCallback, useEffect, useState } from "react";
import { Header, Message, Icon } from "semantic-ui-react";
import { AdminUsersV2 } from "../../../api";
import { useAuth } from "../../../hooks";
import {
  UsersV2Filters,
  UsersV2Table,
  UsersV2Drawer,
  UsersV2DeleteModal,
  UsersV2EditModal,
  UsersV2InstitutionsSimulator,
} from "../../../components/Admin/UsersV2";
import "./UsersV2.scss";

const usersV2Api = new AdminUsersV2();

/**
 * Pagina admin v2 del centro de identidad de usuarios (M05).
 *
 * Vive en /admin/users-v2 — paralela a /admin/users (legacy intacto).
 * Combina:
 *   - filtros (search, roles[], active, dates, orphans)
 *   - tabla densa con paginacion
 *   - drawer de perfil 360
 *   - modal de eliminacion 3-niveles
 *
 * Stack: Semantic UI + tailwind para layout. Source-of-truth de filtros vive
 * en `filters` state — los componentes hijos son controlados.
 */
export function UsersV2() {
  const { accessToken } = useAuth();
  const [filters, setFilters] = useState({ page: 1, limit: 25 });
  const [data, setData] = useState({ users: [], extras: {}, pagination: null });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [drawerUserId, setDrawerUserId] = useState(null);
  const [deleteUser, setDeleteUser] = useState(null);
  const [editUser, setEditUser] = useState(null);

  const fetchList = useCallback(async () => {
    if (!accessToken) return;
    try {
      setLoading(true);
      setError(null);
      const response = await usersV2Api.list(accessToken, filters);
      setData({
        users: response.users || [],
        extras: response.extras || {},
        pagination: response.pagination || null,
      });
    } catch (e) {
      setError(e?.message || "Error obteniendo el listado.");
      setData({ users: [], extras: {}, pagination: null });
    } finally {
      setLoading(false);
    }
  }, [accessToken, filters]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const handleDeleted = () => {
    setDeleteUser(null);
    setDrawerUserId(null);
    fetchList();
  };

  const handleUpdated = () => {
    setEditUser(null);
    fetchList();
  };

  const total = data.pagination?.total || 0;
  const totalPages = data.pagination?.totalPages || 1;

  return (
    <div className="users-v2-page">
      <div className="users-v2-page__header">
        <div>
          <Header as="h2" style={{ marginBottom: 4 }}>
            <Icon name="users" color="blue" />
            <Header.Content>
              Centro de identidad de usuarios
              <Header.Subheader>
                Listado multi-rol con verticales, edicion segura y cascade
                delete controlado.
              </Header.Subheader>
            </Header.Content>
          </Header>
        </div>
        <div className="users-v2-page__header-meta">
          {data.pagination?.total !== undefined && (
            <span>
              <strong>{data.pagination.total.toLocaleString("es-ES")}</strong>{" "}
              usuarios totales
            </span>
          )}
        </div>
      </div>

      <UsersV2Filters filters={filters} onChange={setFilters} total={total} />

      <UsersV2InstitutionsSimulator
        institutions={data.extras?.institutionsSimulator || []}
      />

      {error && (
        <Message negative>
          <Message.Header>Error</Message.Header>
          <p>{error}</p>
        </Message>
      )}

      <UsersV2Table
        data={data.users}
        loading={loading}
        page={filters.page || 1}
        totalPages={totalPages}
        onPageChange={(page) => setFilters({ ...filters, page })}
        onSelect={(user) => setDrawerUserId(user._id)}
        onDelete={(user) => setDeleteUser(user)}
      />

      <UsersV2Drawer
        userId={drawerUserId}
        open={!!drawerUserId}
        onClose={() => setDrawerUserId(null)}
        onDelete={(user) => {
          setDeleteUser(user);
          setDrawerUserId(null);
        }}
        onEdit={(user) => {
          setEditUser(user);
          setDrawerUserId(null);
        }}
      />

      <UsersV2DeleteModal
        user={deleteUser}
        open={!!deleteUser}
        onClose={() => setDeleteUser(null)}
        onDeleted={handleDeleted}
      />

      <UsersV2EditModal
        user={editUser}
        open={!!editUser}
        onClose={() => setEditUser(null)}
        onUpdated={handleUpdated}
      />
    </div>
  );
}
